import { CalendarDate } from "@internationalized/date";
import { useCallback, useMemo, type FC } from "react";
import { useNow } from "../timestamps/timestampContext.js";
import {
  DateRangePickerLayout,
  type CalendarDateRange,
  type DateRangePreset,
} from "./dateRangePickerLayout.js";
import {
  convertForwardDateRangeToCalendarDays,
  type ForwardDateRange,
} from "./forwardDateRange.js";

export interface ForwardDateRangeDialogProps {
  value: ForwardDateRange;
  onChange: (newValue: ForwardDateRange) => void;
}

const PRESETS: readonly DateRangePreset<ForwardDateRange>[] = [
  {
    key: "any",
    textValue: "any",
    label: "At any time",
    value: { type: "any" },
    checkIsSelected: (value) => value.type === "any",
  },
  ...[7, 14, 30, 90].map((days): DateRangePreset<ForwardDateRange> => ({
    key: `next_${days}_days`,
    textValue: `${days}d`,
    label: `Within next ${days} days`,
    value: { type: "next_days", days },
    checkIsSelected: (value) =>
      value.type === "next_days" && value.days === days,
  })),
];

/**
 * The forward counterpart of `DateRangeDialog`, for upcoming dates: the same
 * layout, with presets that run from now (overdue included) and a calendar
 * that starts at today.
 */
export const ForwardDateRangeDialog: FC<ForwardDateRangeDialogProps> = (
  props,
) => {
  const now = useNow();
  const today = useMemo(
    () => new CalendarDate(now.year, now.month, now.day),
    [now],
  );

  const convertToCalendarValue = useCallback(
    (value: ForwardDateRange): CalendarDateRange =>
      convertForwardDateRangeToCalendarDays(value, now),
    [now],
  );

  return (
    <DateRangePickerLayout
      value={props.value}
      onChange={props.onChange}
      presets={PRESETS}
      minCalendarValue={today}
      selectionAlignment="start"
      convertToCalendarValue={convertToCalendarValue}
      convertFromCalendarValue={convertFromCalendarValue}
    />
  );
};

function convertFromCalendarValue(
  calendarValue: CalendarDateRange,
): ForwardDateRange {
  // Typing one bound past the other would otherwise leave an inverted range.
  const [start, end] =
    calendarValue.start.compare(calendarValue.end) <= 0
      ? [calendarValue.start, calendarValue.end]
      : [calendarValue.end, calendarValue.start];

  return { type: "custom", start: start.toString(), end: end.toString() };
}
